import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { applyWricefUsageRule, getWricefUsageRule } from "@/api/wricef-decisions";
import { diagnosticKeys } from "@/hooks/useDiagnostic";

/*
  A regra de "Fora de escopo por falta de uso" no Catálogo WRICEF (#1622).

  Arquivo próprio pelo mesmo motivo de `useWricefDecisions.ts`: os testes da aba
  mockam aquele módulo inteiro, e um hook novo lá dentro sumiria nesses mocks e
  derrubaria a barra da lista. As chaves repetem o prefixo das decisões de
  propósito, para a gravação em lote invalidar a prévia junto.
*/

/** O prefixo de `wricefDecisionKeys.all` (`useWricefDecisions.ts`). */
const decisionsKey = (projectId: string) => ["wricef-decisions", projectId] as const;

/** A chave da lista do WRICEF Catalog, sem a consulta (`useWricefCatalog.ts`). */
const catalogListKey = (projectId: string) => ["wricef-catalog", projectId] as const;

export const wricefUsageRuleKey = (projectId: string) =>
  [...decisionsKey(projectId), "_usage_rule"] as const;

/**
 * A prévia da regra, lida só quando a janela abre: ela recalcula o plano no
 * servidor, e não faz sentido pagar isso a cada visita ao catálogo.
 */
export function useWricefUsageRule(projectId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: wricefUsageRuleKey(projectId ?? ""),
    queryFn: () => getWricefUsageRule(projectId!),
    enabled: !!projectId && enabled,
    staleTime: 0,
  });
}

/** Aplica a regra e relê o que a decisão em lote muda na tela. */
export function useApplyWricefUsageRule(projectId: string | undefined) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (executionId: string | null | undefined) =>
      applyWricefUsageRule(projectId!, executionId),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: diagnosticKeys.snapshot(projectId ?? "") }),
        client.invalidateQueries({ queryKey: decisionsKey(projectId ?? "") }),
        client.invalidateQueries({ queryKey: catalogListKey(projectId ?? "") }),
      ]),
  });
}
